import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ClaimStudioInstance } from '@/api/models/studioInstance';
import { getExtensionAddress, getTelemetryInstanceState, InstanceState } from '@/common/util';
import ClaimExtensionContract, { Claim, ClaimType } from '@/contracts/claimExtensionContract';
import useMainStore from '@/store/mainStore';

/**
 * Primary hook for accessing and refreshing claim data.
 * Provides a unified interface to work with claim data.
 *
 * Features:
 * - Automatic polling every 2 minutes
 * - Real-time updates via blockchain events
 * - Manual refresh via refreshClaim()
 * - Loading state indicator
 *
 * @returns Object containing the claim data and a function to manually refresh it
 */
export function useClaim(): {
  claim: Claim | null;
  refreshClaim: () => Promise<void>;
  isLoading: boolean;
} {
  const studioInstance = useMainStore((state) => state.studioInstance);
  const claim = useMainStore((state) => state.claimData);
  const setClaimData = useMainStore((state) => state.setClaimData);

  const contractInitialized = useRef(false);
  const contractRef = useRef<ClaimExtensionContract | null>(null);

  // Determine if we're in a loading state
  const isLoading = useMemo(() => {
    return studioInstance !== undefined && claim === null;
  }, [studioInstance, claim]);

  // Initialize contract only once when studioInstance is available
  useEffect(() => {
    if (!studioInstance || contractInitialized.current) return;

    try {
      const extensionAddress = getExtensionAddress(studioInstance);
      const newContract = new ClaimExtensionContract(
        studioInstance.publicData.network,
        extensionAddress,
        studioInstance.publicData.contract.contractAddress,
        parseInt(
          (studioInstance as ClaimStudioInstance).publicData?.claimIndex?.toString() ||
            studioInstance.id,
        ),
      );
      contractRef.current = newContract;
      contractInitialized.current = true;
    } catch (error) {
      console.warn('Could not initialize claim contract:', error);
    }
  }, [studioInstance]);

  // Function to refresh claim data
  const refreshClaim = useCallback(async () => {
    if (!studioInstance || !contractRef.current) return;

    try {
      // Determine claim type based on contract spec
      const claimType =
        studioInstance.publicData.contract.spec === 'ERC721' ? ClaimType.ERC721 : ClaimType.ERC1155;

      const claimData = await contractRef.current.getClaim(claimType);

      // Update the global Zustand store with the new claim data
      setClaimData(claimData ?? null);
    } catch (error) {
      console.warn('Could not fetch claim on chain:', error);
    }
  }, [studioInstance, setClaimData]);

  // Set up polling and blockchain event listeners
  useEffect(() => {
    if (!studioInstance || !contractRef.current) return;

    // Initial fetch if we don't have claim data yet or it's been more than 2 minutes
    if (!claim) {
      refreshClaim();
    }
  }, [studioInstance, claim, refreshClaim]);

  return { claim, refreshClaim, isLoading };
}

/**
 * @important uses on chain data as the primary source of truth,
 * falls back to the instance data if on chain data is not available.
 */
export function useInstanceStatus(): InstanceState {
  const studioInstance = useMainStore((state) => state.studioInstance);
  const { claim, isLoading } = useClaim();
  const [instanceState, setInstanceState] = useState<InstanceState>(InstanceState.Upcoming);

  // Calculate status in useEffect to prevent state updates during render
  useEffect(() => {
    if (isLoading) {
      setInstanceState(InstanceState.Loading);
      return;
    }

    if (!studioInstance || !claim) {
      setInstanceState(InstanceState.Upcoming);
      return;
    }

    // Use the utility function to determine state
    const currentState = getTelemetryInstanceState(studioInstance, claim);
    setInstanceState(currentState);
  }, [studioInstance, claim, isLoading]);

  return instanceState;
}

/**
 * Helper hook to check if the current instance is an open edition
 */
export function useIsOpenEdition(): boolean {
  const studioInstance = useMainStore((state) => state.studioInstance);
  const { claim } = useClaim();

  return useMemo(() => {
    // Check if total max is null or 0, indicating an open edition
    return (
      studioInstance !== undefined &&
      (claim?.totalMax === null ||
        claim?.totalMax === 0 ||
        // @ts-expect-error - totalLimit might exist on some instance types
        studioInstance.publicData?.totalLimit === 0)
    );
  }, [studioInstance, claim]);
}

/**
 * Hook to get the instance link for the current studio instance
 * Constructs the URL using creator ID and instance ID
 *
 * @returns The instance link URL as a string, or empty string if data is missing
 */
export function useInstanceLink(): string {
  const studioInstance = useMainStore((state) => state.studioInstance);

  return useMemo((): string => {
    if (!studioInstance?.creator?.id || !studioInstance?.id) {
      console.warn('Missing creator ID or instance ID for URL generation');
      return '';
    }
    return `https://manifold.xyz/@${studioInstance.creator.id}/id/${studioInstance.id}`;
  }, [studioInstance]);
}
